import React, { useCallback, useContext, useState } from 'react'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import RadioInput from 'LuxKit/Radio/RadioInput'
import Heading from 'LuxKit/Typography/Heading'
import { ModalElementContext } from 'contexts/ModalProvider'
import { capitalise } from 'lib/string/stringUtils'
import type {
  MyTripsFilterOptions,
  MyTripsSortOptions,
} from 'tripPlanner/pages/myTrips/MyTripsListGrid'
import {
  MY_TRIPS_FILTER_OPTIONS,
  MY_TRIPS_SORT_OPTIONS,
} from 'tripPlanner/pages/myTrips/MyTripsListGrid'

interface Props {
  onFilterApply: (opt: MyTripsFilterOptions | null) => void
  onSortApply: (opt: MyTripsSortOptions) => void
  selectedSortOption: MyTripsSortOptions
  selectedFilter: MyTripsFilterOptions | null
  isFilterVisible: boolean
}

export default function MyTripsSortAndFilter({
  onFilterApply,
  onSortApply,
  selectedSortOption,
  selectedFilter,
  isFilterVisible,
}: Props) {
  const modalElement = useContext(ModalElementContext)
  const [sortOption, setSortOption] =
    useState<MyTripsSortOptions>(selectedSortOption)
  const [filter, setFilter] = useState<MyTripsFilterOptions | null>(
    selectedFilter,
  )

  const onApply = useCallback(() => {
    onFilterApply(filter)
    onSortApply(sortOption)
    modalElement?.resolve()
  }, [modalElement, filter, onFilterApply, onSortApply, sortOption])

  const onClickFilter = useCallback(
    (opt: MyTripsFilterOptions | null) => {
      if (opt === filter) {
        setFilter(null)
      } else {
        setFilter(opt)
      }
    },
    [filter, setFilter],
  )

  return (
    <>
      <ModalHeader title={isFilterVisible ? 'Sort & Filter' : 'Sort by'} />
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={24}>
            {isFilterVisible && <Heading variant="heading5">Sort by</Heading>}
            <VerticalSpacer gap={12}>
              {MY_TRIPS_SORT_OPTIONS.map((opt) => (
                <div key={opt}>
                  <RadioInput
                    onClick={() => setSortOption(opt)}
                    checked={opt === sortOption}
                  >
                    {capitalise(opt)}
                  </RadioInput>
                </div>
              ))}
            </VerticalSpacer>
            {isFilterVisible && (
              <>
                <Heading variant="heading5">Filter by</Heading>
                <VerticalSpacer gap={12}>
                  {MY_TRIPS_FILTER_OPTIONS.map((opt) => (
                    <div key={opt}>
                      <RadioInput
                        onClick={() => onClickFilter(opt)}
                        checked={filter === opt}
                      >
                        {capitalise(opt)}
                      </RadioInput>
                    </div>
                  ))}
                </VerticalSpacer>
              </>
            )}
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: 'Apply',
          onClick: onApply,
        }}
      />
    </>
  )
}
